/**
 * Whether a live path is actually working — and if not, why.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Names the CAUSE rather than a generic "disabled", because the two causes take
 * different fixes: "Off" is the tracker's on/off toggle, "No traffic" is a
 * weight of 0. A separate badge from TrackerStateBadge, which answers a
 * different question on this same row (is it on the tracker at all) and is
 * shared with three bank screens.
 */

import { FC } from 'react';
import { Ban, CircleSlash } from 'lucide-react';

import { Badge } from '@/components/ui';

import { pathDisabledReason } from './path-status';
import type { QueueElement } from '@trafficpulse/shared';

const COPY = {
  off: {
    label: 'Off',
    icon: Ban,
    title: 'Switched off on the tracker — it sits in the flow but never serves.',
  },
  'no-traffic': {
    label: 'No traffic',
    icon: CircleSlash,
    title: 'Weight 0 on the tracker — in the flow, but receiving nothing.',
  },
} as const;

/** Renders nothing for a healthy path — only the exceptions earn a badge. */
export const PathStatusBadge: FC<{ element: QueueElement }> = ({ element }) => {
  const reason = pathDisabledReason(element);
  if (!reason) return null;
  const { label, icon: Icon, title } = COPY[reason];
  return (
    // Amber, not grey: these rows are the point of the feature, and a muted
    // chip is easy to miss among forty healthy ones.
    <Badge tone="warning" className="gap-1 whitespace-nowrap" title={title}>
      <Icon className="h-3 w-3 shrink-0" aria-hidden />
      {label}
    </Badge>
  );
};

export default PathStatusBadge;
